import { useState } from 'react';
import { forgotPassword } from '../../api/engineDashboard';

interface Props {
  signingIn: boolean;
  error?: string;
  onSignIn: (email: string, password: string) => void;
}

/**
 * Email + password sign-in against the Resolution Engine (mediator, admin,
 * clerk and judge accounts). The Zoom OAuth path is meeting-only and never
 * appears here.
 */
export function LoginPage({ signingIn, error, onSignIn }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState<'signin' | 'forgot' | 'forgot-sent'>('signin');
  const [sending, setSending] = useState(false);

  const canSubmit = email.trim().length > 0 && password.length > 0 && !signingIn;

  const sendReset = async () => {
    setSending(true);
    try {
      await forgotPassword(email.trim());
    } catch {
      /* endpoint always answers 200; a network error still shouldn't leak
         whether the account exists, so show the same confirmation */
    }
    setSending(false);
    setMode('forgot-sent');
  };

  if (mode !== 'signin') {
    return (
      <div className="flex min-h-full flex-col items-center justify-center bg-surface-muted p-6">
        <form
          className="card w-full max-w-sm p-6"
          onSubmit={(e) => {
            e.preventDefault();
            if (mode === 'forgot' && email.trim() && !sending) void sendReset();
          }}
        >
          <div className="text-center">
            <div className="text-2xl font-bold tracking-tight text-brand">Levelheaded</div>
            <div className="mt-1 text-sm text-gray-500">Reset your password</div>
          </div>
          {mode === 'forgot-sent' ? (
            <p className="mt-6 text-center text-sm text-gray-600">
              If an account exists for <strong>{email.trim()}</strong>, a reset link is on its way.
              The link is valid for one hour.
            </p>
          ) : (
            <>
              <label className="mt-6 block">
                <span className="text-xs font-medium text-gray-600">Email</span>
                <input
                  type="email"
                  autoComplete="email"
                  className="mt-1 w-full rounded-md border border-surface-border px-3 py-2 text-sm focus:border-brand focus:outline-none"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={sending}
                />
              </label>
              <button
                type="submit"
                className="btn-primary mt-5 w-full"
                disabled={email.trim().length === 0 || sending}
              >
                {sending ? 'Sending…' : 'Email me a reset link'}
              </button>
            </>
          )}
          <button
            type="button"
            className="mt-4 w-full text-center text-xs text-gray-500 hover:text-gray-800"
            onClick={() => setMode('signin')}
          >
            Back to sign-in
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-col items-center justify-center bg-surface-muted p-6">
      <form
        className="card w-full max-w-sm p-6"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) onSignIn(email, password);
        }}
      >
        <div className="text-center">
          <div className="text-2xl font-bold tracking-tight text-brand">Levelheaded</div>
          <div className="mt-1 text-sm text-gray-500">Mediator workspace sign-in</div>
        </div>

        <label className="mt-6 block">
          <span className="text-xs font-medium text-gray-600">Email</span>
          <input
            type="email"
            autoComplete="email"
            className="mt-1 w-full rounded-md border border-surface-border px-3 py-2 text-sm focus:border-brand focus:outline-none"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={signingIn}
          />
        </label>

        <label className="mt-3 block">
          <span className="text-xs font-medium text-gray-600">Password</span>
          <input
            type="password"
            autoComplete="current-password"
            className="mt-1 w-full rounded-md border border-surface-border px-3 py-2 text-sm focus:border-brand focus:outline-none"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={signingIn}
          />
        </label>

        {error && <div className="mt-3 text-sm text-red-600">{error}</div>}

        <button type="submit" className="btn-primary mt-5 w-full" disabled={!canSubmit}>
          {signingIn ? 'Signing in…' : 'Sign in'}
        </button>

        <button
          type="button"
          className="mt-3 w-full text-center text-xs text-gray-500 hover:text-gray-800"
          onClick={() => setMode('forgot')}
        >
          Forgot password?
        </button>

        <p className="mt-4 text-center text-xs text-gray-400">
          Need an account? Ask your Levelheaded administrator.
        </p>
      </form>
    </div>
  );
}
